import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { ToastrService } from 'ngx-toastr';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { DialogData } from '@edtech-ui/core/models/interfaces/attempt-Quiz/quiz-Question';
import { GetQuizByAttemptIdResponse } from '@edtech-ui/core/models/interfaces/attempt-Quiz/quiz-Question';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-view-quiz-result-dialog',
  standalone: true,
  imports: [CommonModule, MatButtonModule],
  templateUrl: './view-quiz-result-dialog.component.html',
  styleUrls: ['./view-quiz-result-dialog.component.scss']
})
export class ViewQuizResultDialogComponent implements OnInit ,OnDestroy {
  quizData: GetQuizByAttemptIdResponse['Data'] | null = null;
  loading = true;
  subscription =new Subscription();

  constructor(
    private dialogRef: MatDialogRef<ViewQuizResultDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private quizService: QuizManagementService,
    private toastr: ToastrService,
    private router: Router,
	private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    if (this.data.attemptId) {
      this.fetchQuizResults();
    } else {
      this.toastr.error('Attempt ID is missing');
      this.loading = false;
    }
  }

  fetchQuizResults() {
    this.loading = true;
	this.subscription.add(
    this.quizService.getQuizByAttemptId(this.data.attemptId).subscribe({
      next: (response: GetQuizByAttemptIdResponse) => {
        if (response.Success && response.Data) {
          this.quizData = response.Data;
          this.loading = false;
        } else {
          this.toastr.error(response.Message || 'Failed to fetch quiz results');
          this.loading = false;
        }
      },
      error: (error) => {
        this.toastr.error('Failed to fetch quiz results');
        this.loading = false;
      }
    }));
  }

  get quizName(): string {
    return this.quizData?.quiz_name || 'Quiz Completed';
  }

  get score_percentage(): number {
    if (!this.quizData?.overall_user_performance?.score_percentage) return 0;
    return parseInt(this.quizData.overall_user_performance.score_percentage.replace('%', ''));
  }

  get totalQuestions(): number {
    return this.quizData?.overall_user_performance?.total_number_of_question || 0;
  }

  get correctAnswers(): number {
    return this.quizData?.overall_user_performance?.total_number_of_correct_answer || 0;
  }

  get wrongAnswers(): number {
    return this.quizData?.overall_user_performance?.total_number_of_incorrect_answer || 0;
  }

  get skippedAnswers(): number {
    return this.quizData?.overall_user_performance?.total_number_of_skipped_answer || 0;
  }

  get partialCorrectAnswers(): number {
    return this.quizData?.overall_user_performance?.total_number_of_partial_correct_answer || 0;
  }

  get gainedPoints(){
		return this.quizData?.overall_user_performance?.gained_points || 0;
	}

  get timeTaken(): string {
    if (!this.quizData?.time_taken) return '00:00:00';
    const totalSeconds = this.quizData.time_taken;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  get strong_areas(): string[] {
    if (!this.quizData?.quiz_conversation) return [];
    const correctQuestions = this.quizData.quiz_conversation
      .filter(item => item.evaluation.answer_type === 'correct')
      .map(item => item.questions.question_type);
    return [...new Set(correctQuestions)];
  }

  get weakAreas(): string[] {
    if (!this.quizData?.quiz_conversation) return [];
    const incorrectQuestions = this.quizData.quiz_conversation
      .filter(item => item.evaluation.answer_type === 'incorrect')
      .map(item => item.questions.question_type);
    return [...new Set(incorrectQuestions)];
  }

  get overall_feedback(): string {
    return this.quizData?.overall_user_performance?.overall_feedback || '';
  }

	viewDetailedResult() {
		const { attemptId, classId, courseId } = this.data;
		if (!attemptId || !classId || !courseId) {
			this.toastr.error('Missing navigation data');
			return;
		}
		this.router.navigate(['/student-dashboard', 'course-detail-page', classId, courseId, 'detailed-solutions', attemptId]);
		this.dialogRef.close();
	}

  onClose(): void {
    this.dialogRef.close();
  }

  backToQuizPage() {
		this.dialogRef.close();
		const { classId, courseId } = this.data;
		this.router.navigate(['/student-dashboard/course-detail-page',classId,courseId,'quiz-list']);
	}

  ngOnDestroy(): void {
	this.subscription.unsubscribe();
  }
}